/** Default sort used by Admin → Users tables (client-side only). */
export const ADMIN_USERS_LIST_DEFAULTS = {
  size: 10,
  sortBy: 'name',
  sortDir: 'asc',
};

/** Sort keys exposed by the admin users table column headers. */
export const ADMIN_USERS_SORT_FIELDS = {
  name: 'name',
  email: 'email',
  role: 'role',
  status: 'status',
  enrollments: 'enrollments',
};

export const ADMIN_USER_KINDS = {
  instructor: 'instructor',
  learner: 'learner',
  system: 'system',
};

/** Normalizes a ?kind= query value to a supported application-user tab. */
export function resolveApplicationUserKind(kindParam) {
  if (kindParam === ADMIN_USER_KINDS.learner) {
    return ADMIN_USER_KINDS.learner;
  }
  if (kindParam === ADMIN_USER_KINDS.system) {
    return ADMIN_USER_KINDS.system;
  }
  return ADMIN_USER_KINDS.instructor;
}

/**
 * Computes the next client-side sort when a column header is clicked.
 */
export function getNextAdminUsersSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || ADMIN_USERS_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a paginated org admin users query.
 */
export function buildAdminUsersListCacheKey(scope, scopeId, {
  kind = ADMIN_USER_KINDS.instructor,
  page = 0,
  size = ADMIN_USERS_LIST_DEFAULTS.size,
  keyword = '',
} = {}) {
  const kw = (keyword || '').trim();
  return `admin-users:${scope}:${scopeId}:${kind}:${page}:${size}:${kw}`;
}

/**
 * Builds query string for paginated org/system user list API calls.
 */
export function buildAdminUsersListQuery({
  adminUserId,
  orgId,
  kind = ADMIN_USER_KINDS.instructor,
  page = 0,
  size = ADMIN_USERS_LIST_DEFAULTS.size,
  keyword = '',
} = {}) {
  const params = new URLSearchParams();
  params.set('adminUserId', adminUserId);
  if (orgId) {
    params.set('orgId', orgId);
  }
  params.set('kind', kind);
  params.set('page', String(page));
  params.set('size', String(size));
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  return params.toString();
}

function displayName(row) {
  const name = `${row?.firstName || ''} ${row?.lastName || ''}`.trim();
  if (name) {
    return name;
  }
  if (row?.email) {
    return row.email.split('@')[0];
  }
  return 'User';
}

/** Resolves a row field value for client-side column sorting. */
function adminUsersSortValue(row, sortBy) {
  switch (sortBy) {
    case ADMIN_USERS_SORT_FIELDS.email:
      return row.email ?? '';
    case ADMIN_USERS_SORT_FIELDS.role:
      return row.role ?? '';
    case ADMIN_USERS_SORT_FIELDS.status:
      return row.status === 'pending' ? -1 : (row.active ?? 0);
    case ADMIN_USERS_SORT_FIELDS.enrollments:
      return row.enrollmentCount ?? 0;
    case ADMIN_USERS_SORT_FIELDS.name:
    default:
      return displayName(row);
  }
}

/** Sorts the current page of admin user rows in memory — no API round-trip. */
export function sortAdminUsersLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || ADMIN_USERS_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = adminUsersSortValue(a, field);
    const bv = adminUsersSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/** Parses paginated admin users list API response. */
export function parseAdminUsersListResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalElements: payload.length,
      totalPages: 1,
      page: 0,
      size: payload.length,
    };
  }

  return {
    content: Array.isArray(payload?.content) ? payload.content : [],
    totalElements: Number(payload?.totalElements ?? 0),
    totalPages: Number(payload?.totalPages ?? 0),
    page: Number(payload?.page ?? 0),
    size: Number(payload?.size ?? ADMIN_USERS_LIST_DEFAULTS.size),
  };
}

/** Display name from org/system user row. */
export function formatAdminUserName(member) {
  return displayName(member);
}
